import React, { useState } from "react";
import axios from "axios";
import { X, Loader2, Video, MapPin, Phone, MoreHorizontal } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITIES = [
  { id: "in_person", icon: MapPin }, { id: "video", icon: Video },
  { id: "phone", icon: Phone }, { id: "other", icon: MoreHorizontal },
];

// [label][input] as one self-contained unit with a fixed-height label row — so
// two fields placed side by side in a 2-column row never desync when one
// label wraps and the other doesn't (root cause of the iPad misalignment bug).
function FieldBlock({ label, className = "", children }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <label className="block text-xs font-semibold text-slate-500 mb-1.5 min-h-[16px] truncate">{label}</label>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">{children}</p>;
}

const fieldInputClass = "w-full h-11 min-w-0 box-border border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]";

// Header/Body/Footer as three fixed flex regions — only the Body scrolls, the
// modal itself never resizes based on its content (fixed width chain: overlay
// centers a box whose width is set once via className, children never widen it).
export default function InterviewFormModal({ application, initialInterview, defaultContactName, onClose, onSaved }) {
  const { t } = useLanguage();
  const isEdit = !!initialInterview;
  const [interview, setInterview] = useState({
    date: initialInterview?.date || "", time: initialInterview?.time || "", end_time: initialInterview?.end_time || "",
    modality: initialInterview?.modality || "in_person",
    location: initialInterview?.location || "", link: initialInterview?.link || "",
    phone: initialInterview?.phone || "", other_details: initialInterview?.other_details || "",
    contact_name: initialInterview?.contact_name || defaultContactName || "",
    contact_role: initialInterview?.contact_role || "",
    timezone: initialInterview?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const timezoneOptions = (() => {
    let opts;
    try { opts = Intl.supportedValuesOf("timeZone"); } catch { opts = []; }
    if (!opts.includes("UTC")) opts = ["UTC", ...opts];
    if (!opts.includes(interview.timezone)) opts = [interview.timezone, ...opts];
    return opts;
  })();

  const set = (patch) => setInterview((v) => ({ ...v, ...patch }));

  const save = async () => {
    setSending(true);
    setError("");
    try {
      const { date, time, end_time, modality, location, link, phone, other_details, contact_name, contact_role, timezone } = interview;
      const body = {
        stage: "interview", message: message.trim(),
        interview: {
          date, time, end_time, modality,
          location: modality === "in_person" ? location : "",
          link: modality === "video" ? link : "",
          phone: modality === "phone" ? phone : "",
          other_details: modality === "other" ? other_details : "",
          contact_name, contact_role, timezone,
        },
      };
      const res = await axios.post(`${API}/applications/${application.application_id}/notify`, body, { headers: getAuthHeaders() });
      onSaved(res.data.notification);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || t("notifyApplicant.sendError"));
    } finally {
      setSending(false);
    }
  };

  const canSave = !!interview.date && !!interview.time;

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-end sm:items-center justify-center" data-testid="interview-form-modal" onClick={onClose}>
      <div
        className="bg-white w-full sm:w-[min(640px,calc(100vw-2rem))] max-h-[92dvh] sm:max-h-[85vh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden box-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER — fixed, never scrolls */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="font-poppins font-bold text-base text-slate-900 truncate" data-testid="interview-form-title">
              {isEdit ? t("company.editInterviewTitle") : t("company.scheduleInterviewTitle")}
            </h3>
            <p className="text-xs text-slate-400 truncate mt-0.5">{application.name}{application.job_title ? ` · ${application.job_title}` : ""}</p>
          </div>
          <button onClick={onClose} data-testid="interview-form-close" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* BODY — the ONLY scrollable region */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-4 space-y-5">
          <div>
            <SectionTitle>{t("interviewForm.sectionWhen")}</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <FieldBlock label={t("notifyApplicant.date")}>
                <input type="date" data-testid="interview-date-input" value={interview.date} onChange={(e) => set({ date: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("notifyApplicant.time")}>
                <input type="time" data-testid="interview-time-input" value={interview.time} onChange={(e) => set({ time: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("notifyApplicant.endTimeOptional")} className="col-span-2 sm:col-span-1">
                <input type="time" data-testid="interview-end-time-input" value={interview.end_time} onChange={(e) => set({ end_time: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("notifyApplicant.timezone")} className="col-span-2 sm:col-span-1">
                <select data-testid="interview-timezone-select" value={interview.timezone} onChange={(e) => set({ timezone: e.target.value })} className={fieldInputClass}>
                  {timezoneOptions.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </FieldBlock>
            </div>
          </div>

          <div>
            <SectionTitle>{t("notifyApplicant.modality")}</SectionTitle>
            <div className="grid grid-cols-2 gap-2.5">
              {MODALITIES.map(({ id, icon: Icon }) => (
                <button key={id} type="button" data-testid={`interview-modality-${id}`}
                  onClick={() => set({ modality: id })}
                  className={`flex items-center justify-center gap-2 h-[52px] min-w-0 px-2 rounded-xl border-2 box-border text-xs font-semibold transition-colors ${interview.modality === id ? "border-[#028CF8] text-[#028CF8] bg-[#028CF8]/5" : "border-slate-200 text-slate-600 bg-white"}`}>
                  <Icon className="h-4 w-4 flex-shrink-0" /> <span className="truncate">{t(`notifyApplicant.modality_${id}`)}</span>
                </button>
              ))}
            </div>
            {interview.modality === "in_person" && (
              <div className="mt-3">
                <input type="text" data-testid="interview-location-input" value={interview.location} onChange={(e) => set({ location: e.target.value })}
                  placeholder={t("notifyApplicant.locationPlaceholder")} className={fieldInputClass} />
              </div>
            )}
            {interview.modality === "video" && (
              <div className="mt-3">
                <input type="url" data-testid="interview-link-input" value={interview.link} onChange={(e) => set({ link: e.target.value })}
                  placeholder={t("interviewForm.linkPlaceholder")} className={fieldInputClass} />
              </div>
            )}
            {interview.modality === "phone" && (
              <div className="mt-3">
                <input type="tel" data-testid="interview-phone-input" value={interview.phone} onChange={(e) => set({ phone: e.target.value })}
                  placeholder={t("interviewForm.phonePlaceholder")} className={fieldInputClass} />
              </div>
            )}
            {interview.modality === "other" && (
              <div className="mt-3">
                <input type="text" data-testid="interview-other-details-input" value={interview.other_details} onChange={(e) => set({ other_details: e.target.value })}
                  placeholder={t("interviewForm.otherDetailsPlaceholder")} className={fieldInputClass} />
              </div>
            )}
          </div>

          <div>
            <SectionTitle>{t("interviewForm.sectionContact")}</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <FieldBlock label={t("notifyApplicant.contactName")}>
                <input type="text" data-testid="interview-contact-name-input" value={interview.contact_name} onChange={(e) => set({ contact_name: e.target.value })} className={fieldInputClass} />
              </FieldBlock>
              <FieldBlock label={t("notifyApplicant.contactRole")}>
                <input type="text" data-testid="interview-contact-role-input" value={interview.contact_role} onChange={(e) => set({ contact_role: e.target.value })}
                  placeholder={t("notifyApplicant.contactRolePlaceholder")} className={fieldInputClass} />
              </FieldBlock>
            </div>
          </div>

          <div>
            <SectionTitle>{t("interviewForm.sectionMessage")}</SectionTitle>
            <textarea rows={3} data-testid="interview-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder={t("interviewForm.messagePlaceholder")}
              className="w-full min-w-0 box-border border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none" />
          </div>

          {error && <p className="text-sm text-red-600" data-testid="interview-form-error">{error}</p>}
        </div>

        {/* FOOTER — fixed, never scrolls */}
        <div className="flex flex-col sm:flex-row gap-2.5 px-5 py-4 border-t border-slate-100 flex-shrink-0" style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} data-testid="interview-form-cancel" disabled={sending}
            className="w-full sm:flex-1 sm:order-1 h-12 sm:h-11 rounded-xl border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            {t("interviewForm.cancel")}
          </button>
          <button onClick={save} disabled={sending || !canSave} data-testid="interview-form-save"
            className="w-full sm:flex-1 sm:order-2 h-12 sm:h-11 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
            {sending && <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />}
            <span className="truncate">{isEdit ? t("company.saveInterviewChanges") : t("company.scheduleInterviewBtn")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
